/**
 * @file apiClient.js
 * @description Cliente HTTP para comunicação com API (CRA)
 */

import { API_CONFIG } from "./config/constants.js"
import { getAuthHeaders } from "./interceptors/auth.js"
import { handleFetchError } from "./interceptors/error-handler.js"

/**
 * Cria headers padrão para requisições
 * @returns {Promise<HeadersInit>} Headers padrão
 */
async function getDefaultHeaders() {
  const authHeaders = await getAuthHeaders()

  return {
    ...API_CONFIG.defaultHeaders,
    ...authHeaders,
  }
}

/**
 * @class ApiClient
 * @description Cliente HTTP para comunicação com API usando fetch nativo
 */
export class ApiClient {
  constructor(baseURL) {
    this.baseURL = baseURL || API_CONFIG.baseURL
  }

  /**
   * Executa requisição com autenticação e tratamento de erros
   * @param {string} endpoint - Endpoint da API
   * @param {Object} options - Opções de requisição
   * @returns {Promise<*>} Resposta da API
   * @private
   */
  async fetchWithAuth(endpoint, options = {}) {
    const url = endpoint.startsWith("http")
      ? endpoint
      : `${this.baseURL}${endpoint}`

    const defaultHeaders = await getDefaultHeaders()
    const customHeaders = options.headers || {}
    const headers = {
      ...defaultHeaders,
      ...customHeaders,
    }

    const config = {
      ...options,
      headers,
    }

    if (process.env.NODE_ENV === "development") {
      console.log(`🌐 Fetch: ${options.method || "GET"} ${url}`)
    }

    const response = await fetch(url, config)

    if (!response.ok) {
      await handleFetchError(response)
    }

    if (response.status === 204) {
      return null
    }

    const rawText = await response.text()
    if (!rawText || rawText.trim() === "") {
      return null
    }

    try {
      return JSON.parse(rawText)
    } catch (error) {
      console.error("Error parsing JSON response:", error)
      throw error
    }
  }

  /**
   * GET request
   * @param {string} endpoint - Endpoint da API
   * @param {Object} options - Opções de requisição
   * @returns {Promise<*>} Resposta da API
   */
  async get(endpoint, options = {}) {
    return this.fetchWithAuth(endpoint, {
      method: "GET",
      ...options,
    })
  }

  /**
   * POST request (suporta JSON e FormData)
   * @param {string} endpoint - Endpoint da API
   * @param {*} data - Dados (object ou FormData)
   * @param {Object} options - Opções de requisição
   * @returns {Promise<*>} Resposta da API
   */
  async post(endpoint, data, options = {}) {
    // Detecta FormData e não faz stringify
    const body =
      data instanceof FormData ? data : data ? JSON.stringify(data) : undefined

    // Se FormData, remove Content-Type (browser adiciona com boundary)
    const headers =
      data instanceof FormData ? {} : API_CONFIG.defaultHeaders

    return this.fetchWithAuth(endpoint, {
      method: "POST",
      body,
      cache: "no-store",
      ...options,
      headers: { ...headers, ...options.headers },
    })
  }

  /**
   * PUT request
   * @param {string} endpoint - Endpoint da API
   * @param {*} data - Dados a serem enviados
   * @param {Object} options - Opções de requisição
   * @returns {Promise<*>} Resposta da API
   */
  async put(endpoint, data, options = {}) {
    return this.fetchWithAuth(endpoint, {
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
      cache: "no-store",
      ...options,
    })
  }

  /**
   * PATCH request
   * @param {string} endpoint - Endpoint da API
   * @param {*} data - Dados a serem enviados
   * @param {Object} options - Opções de requisição
   * @returns {Promise<*>} Resposta da API
   */
  async patch(endpoint, data, options = {}) {
    return this.fetchWithAuth(endpoint, {
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
      cache: "no-store",
      ...options,
    })
  }

  /**
   * DELETE request
   * @param {string} endpoint - Endpoint da API
   * @param {Object} options - Opções de requisição (pode incluir body)
   * @returns {Promise<*>} Resposta da API
   */
  async delete(endpoint, options = {}) {
    const config = {
      method: "DELETE",
      cache: "no-store",
      ...options,
    }
    
    // Se options contém body como objeto, stringify
    if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData)) {
      config.body = JSON.stringify(options.body)
      config.headers = {
        ...API_CONFIG.defaultHeaders,
        ...options.headers,
      }
    }
    
    return this.fetchWithAuth(endpoint, config)
  }
}

/**
 * Instância singleton para uso conveniente no código da aplicação
 */
const apiClient = new ApiClient()

export { apiClient }
export default apiClient
